import { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { markIgnoreUntil } from "../../utils/clickGuard";

interface MiniDropdownProps {
  value: string;
  // description 추가 시 옵션 button 안에서 짙은 grey + 작은 폰트로 보조 표시
  options: { key: string; label: string; flag?: string; description?: string }[];
  onChange: (key: string) => void;
  className?: string;
  theme?: "dark" | "light";
  compact?: boolean;
  align?: "left" | "right";
  // 요구사항 #293: 국가용 — 테두리 제거
  noBorder?: boolean;
  // 요구사항 #293: 도시용 — 아래 화살표(ChevronDown) 노출
  showChevron?: boolean;
}

export default function MiniDropdown({ value, options, onChange, className, theme = "dark", compact = false, align = "left", noBorder = false, showChevron = false }: MiniDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.key === value);
  // 옵션이 1개 이하면 클릭/펼침 비활성 (선택지 없음)
  const isSingleOption = options.length <= 1;

  useEffect(() => {
    // 요구사항 #281-2 재요청: outside click 으로 닫을 때 모바일 ghost click 으로
    // 같은 위치의 BannerCard 가 클릭되어 배너 상세 모달이 열리는 결함.
    // markIgnoreUntil 로 후속 click 까지 차단.
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && open) {
        markIgnoreUntil(500);
        setOpen(false);
      }
    };
    const touchHandler = (e: TouchEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && open) {
        markIgnoreUntil(500);
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", touchHandler, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", touchHandler);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const isDark = theme === "dark";

  return (
    <div ref={ref} className={`relative ${className ?? ""}`}>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isSingleOption) {
            // trigger 클릭 직후 ghost click 으로 다른 button 트리거 차단
            markIgnoreUntil(300);
            setOpen(!open);
          }
        }}
        disabled={isSingleOption}
        className={`flex w-full items-center justify-between rounded-lg ${
          compact ? "px-1.5 py-0.5 text-[11px]" : "px-1.5 py-0.5 text-xs md:px-2.5 md:py-1.5 md:text-sm"
        } ${
          noBorder
            ? isDark ? "text-white" : "text-slate-900"
            : isDark
              ? "border border-white/10 bg-white/5 text-white"
              : "border border-slate-300 bg-white text-slate-900"
        } ${isSingleOption ? "cursor-default opacity-90" : ""}`}
      >
        <span className="flex items-center gap-1 truncate min-w-0">
          {selected?.flag && <span className="shrink-0 text-xs">{selected.flag}</span>}
          <span className="truncate">{selected?.label ?? ""}</span>
        </span>
        {showChevron && !isSingleOption && (
          <ChevronDown className={`h-3 w-3 shrink-0 ${isDark ? "text-slate-400" : "text-slate-500"} transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </button>
      {open && (
        <>
          {/* backdrop overlay 제거 — outside click 닫기는 useEffect mousedown 으로, 다른 메뉴/카드 클릭은 즉시 반응. ghost-click 은 옵션 클릭 시 markIgnoreUntil(500) 으로 차단. */}
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
            className={`absolute ${align === "right" ? "right-0" : "left-0"} top-full z-[200] mt-1 min-w-full w-max max-w-[50vw] md:max-w-[90vw] max-h-[60vh] overflow-y-auto overflow-x-hidden rounded-lg border shadow-xl ${
              isDark
                ? "border-white/10 bg-slate-900"
                : "border-slate-200 bg-white"
            }`}
            style={{ transformOrigin: align === "right" ? "top right" : "top", animation: "dropdownOpen 0.2s ease-out forwards" }}
          >
          {options.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onMouseDown={(e) => { e.stopPropagation(); }}
              onTouchStart={(e) => { e.stopPropagation(); }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // iOS Safari ghost click 회피: 옵션 클릭 후 500ms 동안 모든 BannerCard click 무시
                markIgnoreUntil(500);
                onChange(opt.key);
                setTimeout(() => setOpen(false), 100);
              }}
              className={`flex w-full flex-col items-start md:flex-row md:items-center md:gap-1.5 transition-colors ${
                compact ? "px-2 py-1 text-[11px]" : "px-3 py-2 text-sm"
              } ${
                value === opt.key
                  ? isDark ? "bg-amber-400/10 text-amber-400" : "bg-sky-50 text-sky-600"
                  : isDark ? "text-slate-300 hover:bg-white/5" : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <span className="flex items-center gap-1.5 truncate w-full">
                {opt.flag && <span>{opt.flag}</span>}
                <span className="truncate">{opt.label}</span>
                {/* 데스크탑: 인라인 description (한 줄) */}
                {opt.description && (
                  <span className={`hidden md:inline ml-1 text-[9px] truncate ${isDark ? "text-slate-500" : "text-slate-600"}`}>- {opt.description}</span>
                )}
              </span>
              {/* 모바일: description 두번째 줄 (옵션 너비 영향 없음) — 좌단 정렬 + 라벨에 바짝 */}
              {opt.description && (
                <span className={`md:hidden text-[9px] truncate w-full text-left ${isDark ? "text-slate-500" : "text-slate-500"}`}>{opt.description}</span>
              )}
            </button>
          ))}
          </div>
        </>
      )}
    </div>
  );
}
